if (!customElements.get('variant-selects')) {
  // Consecutive renders that needed a fallback, per section. The picker markup
  // is replaced wholesale on every variant change, so connectedCallback runs
  // again on the fresh element -- this stops a render that keeps coming back
  // unselectable from bouncing between fallback and re-render forever. Reset
  // as soon as a render arrives with every option already valid.
  const fallbackRounds = new Map();

  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        // Deferred so <product-info> -- which subscribes to
        // optionValueSelectionChange in its own connectedCallback -- is upgraded
        // and listening before a fallback change event goes out. A timeout runs
        // after every deferred script, whether this element came from the
        // initial parse or from a later section render.
        setTimeout(() => this.selectFallbackValues(), 0);
      }

      // An option group can come back from a render without a usable selection
      // in two ways: nothing checked at all, or -- after a change to another
      // option -- the value that stayed checked is now unavailable in the new
      // combination. Picking Colonial 30 when the current colour only exists for
      // Colonial 23 leaves that swatch checked *and* disabled, and no variant
      // matches. Everything downstream -- the form's input[name="id"], the
      // legend value the imagineIo configurator posts to its iframe -- assumes
      // each option holds a value that actually resolves, so move any such group
      // on to its first available value.
      selectFallbackValues() {
        const groups = Array.from(this.querySelectorAll('.product-form__input'));
        const sectionKey = this.dataset.section || this.id;
        const picked = [];

        groups.forEach((group) => {
          const fallback = group.querySelector('select')
            ? this.pickDropdownFallback(group)
            : this.pickRadioFallback(group);
          if (fallback) picked.push(fallback);
        });

        if (!picked.length) {
          fallbackRounds.delete(sectionKey);
          return;
        }

        // The re-render below rewrites these legends, but the value has to read
        // correctly straight away for anything that posts the current selection
        // before the fetch lands.
        picked.forEach(({ target, value }) => {
          const label = target.closest('.product-form__input').querySelector('[data-selected-swatch-value]');
          if (label) label.textContent = value;
        });

        // Fixing one option can invalidate another, so a couple of rounds are
        // expected -- but each round settles at least one group. Past one round
        // per group the combination is never going to come back clean; keep the
        // local selection and stop asking for another render.
        const rounds = (fallbackRounds.get(sectionKey) || 0) + 1;
        fallbackRounds.set(sectionKey, rounds);
        if (rounds > groups.length) return;

        const { target } = picked[picked.length - 1];
        // product-info reads `target.id` synchronously here and focuses `#id`
        // once the re-render lands. Borrow the id of whatever the user last
        // touched so this second render hands focus back there instead of to a
        // value they never picked; with nothing focused -- an automatic fix on
        // page load -- use an id the new markup won't contain, so the page
        // doesn't scroll to the picker. Restored right after, since the event
        // and the id read off it are both synchronous.
        const focusTarget = this.getInputForEventTarget(target);
        const originalId = focusTarget.id;
        focusTarget.id = (this.contains(document.activeElement) && document.activeElement.id) || 'VariantSelectsFallback';
        target.dispatchEvent(new Event('change', { bubbles: true }));
        focusTarget.id = originalId;
      }

      // Returns { target, value, id } for the value it moved to, or null when the
      // group is already fine / has nothing better to offer.
      pickRadioFallback(group) {
        const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) return null;

        const current = inputs.find((input) => input.checked);
        if (current && this.isSelectable(current)) return null;

        const fallback = this.firstSelectable(inputs, current);
        if (!fallback) return null;

        if (current) current.removeAttribute('checked');
        fallback.checked = true;
        fallback.setAttribute('checked', 'checked');

        return { target: fallback, value: fallback.value, id: fallback.dataset.optionValueId };
      }

      pickDropdownFallback(group) {
        const select = group.querySelector('select');
        const options = Array.from(select.options);
        if (!options.length) return null;

        const current = options.find((option) => option.hasAttribute('selected'));
        if (current && this.isSelectable(current)) return null;

        const fallback = this.firstSelectable(options, current);
        if (!fallback) return null;

        if (current) current.removeAttribute('selected');
        fallback.setAttribute('selected', 'selected');
        select.value = fallback.value;

        return { target: select, value: fallback.value, id: fallback.dataset.optionValueId };
      }

      // Prefer a value that is actually in stock for the rest of the combination.
      // With none left, only fill a group that has no selection at all -- swapping
      // an unavailable value for another unavailable one just churns renders.
      firstSelectable(values, current) {
        const available = values.find((value) => this.isSelectable(value));
        if (available) return available === current ? null : available;

        return current ? null : values.find((value) => !value.disabled) || null;
      }

      // Radios mark a value that is sold out -- or not part of the combination
      // currently selected -- with a `disabled` class, leaving the input itself
      // usable; dropdown options use `data-option-unavailable`. The `disabled`
      // attribute means the value can't be picked at all.
      isSelectable(element) {
        return (
          !element.disabled &&
          !element.classList.contains('disabled') &&
          !element.hasAttribute('data-option-unavailable')
        );
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
